import { Button, CircularProgress } from "@mui/material";
import React, { useContext, useMemo } from "react";
import Draggable from "react-draggable";
import selectableTableIcon from "../../../images/TableSelect.svg";
import { useUpdateTableStyle } from "../../../shared/hooks/useTablesData";
import reservedTableIcon from "./../../../images/table-reserved-icon.svg";
import { useStyles } from "./styles";
import { AppContext } from "../../../AppContext";
const DragableTable = ({
    table,
    handleOpenMenu,
    moving,
    selectable,
    imageWidth,
    imageHeight,
    calculatedImageWidth,
    calculatedImageHeight,
    onTargetTableClicked,
}) => {
    const classes = useStyles();
    const { foodLateTimeEnabled, foodLateTime, loadingTable } =
        useContext(AppContext);

    const updateTableStyleMutation = useUpdateTableStyle();

    const callMenu = (event, table) => {
        if (handleOpenMenu) {
            handleOpenMenu(event, table);
        }
    };

    const amount = useMemo(() => {
        return Math.ceil(
            Object.values(table.orders).reduce(
                (acc, order) => acc + order.amount,
                0
            )
        );
    }, [table]);

    const position = useMemo(() => {
        if (imageWidth * calculatedImageWidth > 0) {
            try {
                const { x, y } = JSON.parse(table.dataStyle);
                const xRatio = imageWidth / calculatedImageWidth;
                const yRatio = imageHeight / calculatedImageHeight;
                return { x: Math.round(x / xRatio), y: Math.round(y / yRatio) };
            } catch (error) {
                console.error("Error parsing table data style:", error);
                return { x: 0, y: 0 };
            }
        }
    }, [
        imageWidth,
        calculatedImageWidth,
        table,
        imageHeight,
        calculatedImageHeight,
    ]);

    const handleDragStop = (event, draggableData) => {
        if (!table) return;
        let { x, y } = draggableData;

        const newStyle = {
            x: Number(x) || 0,
            y: Number(y) || 0,
            width: "100px",
            height: "100px",
            backgroundColor: "#97264f",
        };

        const data = {
            data: JSON.stringify(newStyle),
            shape: false,
        };

        updateTableStyleMutation.mutate({
            tableId: table.name,
            newStyle: data, // Convert the newStyle object to JSON
        });
    };

    const getActionImage = useMemo(() => {
        if (selectable) {
            return <img alt="Table" src={selectableTableIcon} />;
        } else if (table.reserved) {
            return <img alt="Table" src={reservedTableIcon} />;
        }
    }, [selectable]);

    const isPrinted = useMemo(() => {
        return Object.values(table.orders).some((i) => i.invoice_printed == 1);
    }, [table]);

    const parseOrderDate = (value) => {
        if (!value) {
            return null;
        }
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return value;
        }
        const normalized =
            typeof value === "string" ? value.replace(" ", "T") : value;
        const parsedDate = new Date(normalized);
        if (Number.isNaN(parsedDate.getTime())) {
            return null;
        }
        return parsedDate;
    };

    const hasLateOrder = useMemo(() => {
        if (!foodLateTimeEnabled || !table?.orders) {
            return false;
        }
        const thresholdMinutes = Number(foodLateTime);
        if (!thresholdMinutes || thresholdMinutes <= 0) {
            return false;
        }
        const now = Date.now();
        return Object.values(table.orders).some((order) => {
            if (!order || order.food_ready_time) {
                return false;
            }
            const creationValue = order.creation || order.creation_time;
            const createdAt = parseOrderDate(creationValue);
            if (!createdAt) {
                return false;
            }
            const diffMinutes = (now - createdAt.getTime()) / 60000;
            return diffMinutes >= thresholdMinutes;
        });
    }, [table, foodLateTimeEnabled, foodLateTime]);

    const getTableStyle = () => {
        if (hasLateOrder) {
            return `Table_Late`;
        }
        if (isPrinted) {
            return `Table_Printed`;
        }
        if (table.reserved) {
            return `Table_Reserved`;
        } else if (!table.orders || Object.values(table.orders).length === 0) {
            return `Table_Empty`;
        }
        return "Table_";
    };

    const getTableNumbersAndDescription = () => {
        if (table?.numberOfOrders !== 0) {
            return <div>{table.tableDescription}</div>;
        } else {
            return <div>{table.tableDescription}</div>;
        }
    };

    const handleTargetTableClicked = (table) => {
        onTargetTableClicked(table);
    };
    const xRatio = imageWidth / calculatedImageWidth;
    const yRatio = imageHeight / calculatedImageHeight;
    const isTableUpdating = loadingTable === table.name;

    return (
        <>
            {position && (
                <Draggable
                    onStop={handleDragStop}
                    disabled={!moving}
                    key={`Room_Card_Table_${table.name}`}
                    defaultPosition={{
                        x: position.x,
                        y: position.y,
                    }}
                >
                    {selectable ? (
                        <Button
                            style={{
                                width: `${60 * xRatio}px`,
                                height: `${60 * yRatio}px`,
                                // height: "100px",
                            }}
                            className={getTableStyle()}
                            onClick={(event) => {
                                handleTargetTableClicked(table);
                                // callMenu(event, table);
                            }}
                            disabled={isTableUpdating}
                        >
                            {isTableUpdating && (
                                <div className={classes.Table_LoadingOverlay}>
                                    <CircularProgress
                                        size={22}
                                        className={classes.Table_LoadingSpinner}
                                    />
                                </div>
                            )}
                            {table.reserved ? (
                                <div className={classes.Table_selectable}>
                                    <div>{table.tableDescription}</div>
                                    <div>{getActionImage}</div>
                                </div>
                            ) : (
                                <div className={classes.Table_selectable}>
                                    <div>{table.tableDescription}</div>
                                    {getActionImage}
                                </div>
                            )}
                        </Button>
                    ) : (
                        <Button
                            className={getTableStyle()}
                            style={{
                                width: `${60 / yRatio}px`,
                                height: `${60 / xRatio}px`,
                            }}
                            onClick={(event) => callMenu(event, table)}
                            disabled={isTableUpdating}
                        >
                            {isTableUpdating && (
                                <div className={classes.Table_LoadingOverlay}>
                                    <CircularProgress
                                        size={22}
                                        className={classes.Table_LoadingSpinner}
                                    />
                                </div>
                            )}
                            {table.reserved ? (
                                <div className={classes.Table_ReservedWrap}>
                                    <div>{table.tableDescription}</div>
                                    <div>{getActionImage}</div>
                                </div>
                            ) : (
                                getTableNumbersAndDescription()
                            )}
                            {/* {amount > 0 && (
                                <div className={classes.Table_Amount}>
                                    {amount}₪
                                </div>
                            )} */}
                        </Button>
                    )}
                </Draggable>
            )}
        </>
    );
};

export default DragableTable;
